import { Tooltip } from '@/components/ui/tooltip';
import type { ReactNode } from 'react';

/**
 * The state pill of the interview screens (issue #11, §12 of the chat spec).
 *
 * ## It came from `admin/journal-cells.tsx`, and it moved rather than being
 * copied
 *
 * The sessions table used to own it, and the interviews borrowed it so a state
 * would read the same way on both screens. The coupe (issue #32) deletes the
 * sessions table - the page is rewritten by a later lot (§12) - and two of the
 * three cells in that file went with it. Leaving one export behind under a name
 * that describes a screen the app no longer has would have been the worse half
 * of both options, so the badge follows its only remaining caller.
 *
 * `BadgeTone` came with it for the same reason: `journal-row-labels.ts` held it
 * beside the status tables of the sessions, and those tables read a `JournalRow`
 * that no longer exists.
 *
 * No dependency and no chart. The palette stays at level 2 - black, white and
 * bleu-nuit - except where a state MEANS something, and there the noyau is the
 * meaning: vert for a conversation that ended, bleu-ciel for one under way,
 * jaune-deep for one abandoned, rouge nowhere here (nothing in this reading is
 * an irreversible action).
 */

/** The six tones a pill may wear. */
export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: 'bg-gray-100 text-gray-700',
  accent: 'bg-bleu-nuit-tint text-bleu-nuit',
  success: 'bg-vert-tint text-vert-deep',
  // Never white on jaune, and never jaune itself for text: the charte allows
  // yellow to carry text at `-deep` alone.
  warning: 'bg-jaune-tint text-jaune-deep',
  danger: 'bg-rouge-tint text-rouge-deep',
  info: 'bg-bleu-ciel-tint text-bleu-ciel-deep',
};

export interface StatusBadgeProps {
  tone: BadgeTone;
  children: ReactNode;
  /** A slow blink beside the word, for a conversation happening right now. */
  pulse?: boolean;
  /**
   * What the bubble says, for a pill that abbreviates a state (#103).
   *
   * The PROP keeps the name its callers already write; what changed under it is
   * that the sentence is now drawn by `Tooltip` instead of being handed to the
   * browser as a native `title`.
   */
  title?: string;
  className?: string;
  /**
   * The mark a suite reads this pill by, when its screen has one (#84).
   *
   * Optional and forwarded rather than invented per caller: the interview
   * screens find their badge through the row around it, and Réglages > Jetons
   * marks it directly - `jetons-jeton-etat`. A caller that wrapped the badge in
   * a marked `<span>` to get the same thing would put a second element in the
   * accessibility tree for nothing.
   */
  'data-testid'?: string;
}

/** The badge of the mockup: a pill, a tone, and nothing else. */
export function StatusBadge({
  tone,
  children,
  pulse,
  title,
  className,
  'data-testid': testId,
}: StatusBadgeProps) {
  return (
    // Wrapped unconditionally: a pill without a `title` hands the bubble no
    // text, and a bubble with no text does not open.
    <Tooltip label={title}>
      <span
        data-testid={testId}
        className={[
          'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 font-semibold text-xs',
          TONE_CLASS[tone],
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {pulse === true ? (
          // `aria-hidden`: the word beside it already says « en cours », and a
          // second announcement of the same fact is noise in a screen reader.
          <span aria-hidden="true" className="size-1.5 animate-pulse rounded-full bg-current" />
        ) : null}
        {children}
      </span>
    </Tooltip>
  );
}
